import React, {Component} from "react";
import {Control, Field, Icon, Input, Label, Radio, Select} from "bloomer";
import Card from "../../../elements/Card";
import Button from "../../../elements/Button";
import SendGAEvent, {GAEventActions, GAEventModels} from "../../../utils/SendGAEvent";
import InformationCard from "../../../elements/InformationCard";
import Spacer from "../../../elements/Spacer";
import ModelContentContainer from "../../../elements/ModelContentContainer";
import ErrorUtils from "../../../utils/ErrorUtils";
import {toast} from "react-toastify";
import {
    getHistoryWeatherInfo,
    getRealtimeWeatherInfo,
    getWeatherForecastInfo,
    searchWeatherInfo
} from "../../../api/models/WeatherAPI";
import SectionName from "../../../elements/SectionName";
import InputWarning from "../../../elements/InputWarning";
import styled from "react-emotion";
import WeatherForecast from "./WeatherForecast";
import WeatherRealtime from "./WeatherRealtime";

const LocationContainer = styled("p")`
  margin-left: 15px;
`;

const SmallSection = styled("h4")`
  margin: 0px 0px 5px 0px;
  font-size: 14px;
  font-weight: 700;
`;

const SearchResult = styled("div")`
  border-bottom: 1px solid lightgrey;
  display: flex;
  padding: 1px 5px;
  
  :hover {
    cursor: pointer;
    color: white !important;
    background: #2f3241 !important;
  }
`;

const SearchResultContainer = styled("p")`
  overflow-y: auto;
  max-height: 300px;
`;


interface IWeatherModelState {
    loading: boolean;
    showRefreshButton: boolean;
    unitType: string;
    option: string;
    dateFrom: any;
    dateTo: any;
    dateFromInvalid: boolean;
    dateToInvalid: boolean;
    loadingLocations: boolean;
    showSearchResult: boolean;
    locationSearchText: string;
    locations: any;
    selectedLocation: any;
    locationInvalid: boolean;
    result: any;
    errorMessage: string;
}

class WeatherModel extends Component<{}, IWeatherModelState> {
    public DAY = 86400000;
    public locationInvalidFieldStyle = {color: 'red'};
    public timeout: any = 0;
    public dateInputStyle = {
        padding: '6px',
        borderRadius: '3px',
        border: '1px solid lightgrey',
        color: '#4a4a4a'
    };

    public constructor(props: {}) {
        super(props);

        const dateFrom = new Date(new Date().getTime() - 3 * this.DAY);

        this.state = {
            loading: false,
            showRefreshButton: false,
            unitType: "celsius",
            option: "realtime",
            dateFrom: this.formatDate(dateFrom),
            dateFromInvalid: false,
            dateTo: this.formatDate(new Date()),
            dateToInvalid: false,
            loadingLocations: false,
            showSearchResult: false,
            locationSearchText: "",
            locations: [],
            selectedLocation: null,
            locationInvalid: false,
            result: null,
            errorMessage: ""
        };
    }

    public componentDidMount() {
        SendGAEvent(GAEventActions.VIEW_MODEL, GAEventModels.WEATHER);
    }

    public componentWillUnmount() {
        clearTimeout(this.timeout);
    }

    public formatDate = (date: any) => {
        const month = date.getMonth() + 1;
        const day = date.getDate();
        return [
            date.getFullYear(),
            month > 9 ? month : ("0" + month),
            day > 9 ? day : ("0" + day)
        ].join('-');
    };

    public handleOptionChange = (e: any) => {
        this.setState({
            option: e.target.value
        }, () => {
            this.loadData();
        });
    };

    public handleDateFromChange = (e: any) => {
        const date = e.target.value;
        let valid = true;
        if (date) {
            if (new Date(date) > new Date()) {
                valid = false;
            }
            if (this.state.dateTo && new Date(this.state.dateFrom) > date) {
                valid = false;
            }
        }
        this.setState({
            dateFrom: date,
            dateFromInvalid: !valid
        });
    }

    public handleDateToChange = (e: any) => {
        const date = e.target.value;
        let valid = true;
        if (date) {
            if (new Date(date) > new Date()) {
                valid = false;
            }
            if (this.state.dateFrom && new Date(this.state.dateFrom) > date) {
                valid = false;
            }
        }
        this.setState({
            dateTo: date,
            dateToInvalid: !valid
        });
    }

    public handleSelectUnitTypeCelsius = () => {
        this.setState({
            unitType: "celsius"
        });
    }

    public handleSelectUnitTypeFahrenheit = () => {
        this.setState({
            unitType: "fahrenheit"
        });
    }

    public handleLocationFieldFocus = (event: any) => {
        if (this.state.loadingLocations) {
            return;
        }

        let text = "";
        if (this.state.selectedLocation) {
            text = this.state.selectedLocation.name;
        }
        this.setState({
            showSearchResult: true,
            locationSearchText: text
        });
    };

    public handleLocationSearchTextChange = async (e: any) => {
        clearTimeout(this.timeout);
        const searchText = e.currentTarget.value;
        this.setState({
            locationSearchText: searchText,
            showSearchResult: true,
            locationInvalid: false
        }, async () => {
            if (!searchText.trim()) {
                this.setState({
                    selectedLocation: null,
                    locations: []
                });
            } else {
                this.timeout = setTimeout(async () => {
                    this.setState({loadingLocations: true});
                    const response = await searchWeatherInfo(searchText.trim());
                    this.setState({
                        loadingLocations: false
                    });
                    if (response && response.data) {
                        this.setState({
                            locations: response.data,
                            showSearchResult: true
                        });
                    }
                }, 500);
            }
        });
    };

    public selectLocation = (location: any) => {
        this.setState({
            selectedLocation: location,
            loadingLocations: false,
            showSearchResult: false,
            locationSearchText: ""
        }, () => {
            this.loadData();
        });
    };

    public processError = (error: any) => {
        if (ErrorUtils.requestLimitExceeded(error.response)) {
            this.setState({
                loading: false
            });
            toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
        } else {
            this.setState({
                loading: false,
                errorMessage: "Couldn't get data."
            });
        }
    };

    public loadData = async () => {
        if (this.state.loading || !this.state.selectedLocation) {
            this.setState({result: null});
            return;
        }

        this.setState({
            loading: true,
            showRefreshButton: false,
            result: null,
            errorMessage: ""
        });

        let errorOccurred = false;
        let response: any = null;

        const locationName = this.state.selectedLocation.name;

        if (this.state.option === 'realtime') {
            response = await getRealtimeWeatherInfo(locationName).catch((error) => {
                this.processError(error);
                errorOccurred = true;
            });
        } else if (this.state.option === 'history') {
            response = await getHistoryWeatherInfo(locationName, this.state.dateFrom, this.state.dateTo).catch((error) => {
                this.processError(error);
                errorOccurred = true;
            });
        } else if (this.state.option === 'forecast') {
            response = await getWeatherForecastInfo(locationName).catch((error) => {
                this.processError(error);
                errorOccurred = true;
            });
        }

        if (errorOccurred) {
            return;
        }

        if (response && response.data) {
            this.setState({
                loading: false,
                showRefreshButton: true,
                result: response.data
            });
        } else {
            this.setState({
                loading: false,
                errorMessage: "Couldn't get data."
            });
        }

        SendGAEvent(GAEventActions.CALL_MODEL, GAEventModels.WEATHER);
    };

    public onRefresh = (e: any) => {
        e.target.blur();
        this.loadData();
    };

    public onReset = (e: any) => {
        e.target.blur();

        this.setState({
            loading: false,
            showRefreshButton: false,
            unitType: "celsius",
            option: "realtime",
            dateFrom: null,
            dateFromInvalid: false,
            dateTo: null,
            dateToInvalid: false,
            loadingLocations: false,
            showSearchResult: false,
            locationSearchText: "",
            locations: [],
            selectedLocation: null,
            locationInvalid: false,
            result: null,
            errorMessage: ""
        });
    };

    public render() {
        return <ModelContentContainer>
            <Card>
                <Field>
                    <Label>Period:</Label>
                    <Control>
                        <Select
                            onChange={this.handleOptionChange}
                            value={this.state.option}
                        >
                            <option value={"realtime"}>Realtime</option>
                            <option value={"history"}>History</option>
                            <option value={"forecast"}>Forecast</option>
                        </Select>
                    </Control>
                </Field>
                <Spacer/>
                {
                    this.state.option === 'history' ? <React.Fragment>
                        <SectionName>From:</SectionName>
                        <Field>
                            <Control>
                                <input
                                    style={this.dateInputStyle}
                                    type="date"
                                    value={this.state.dateFrom}
                                    onChange={this.handleDateFromChange}
                                />
                            </Control>
                            {
                                this.state.dateFromInvalid ? <InputWarning>Invalid date.</InputWarning> : <></>
                            }
                        </Field>
                        <SectionName>To:</SectionName>
                        <Field>
                            <Control>
                                <input
                                    style={this.dateInputStyle}
                                    type="date"
                                    value={this.state.dateTo}
                                    onChange={this.handleDateToChange}
                                />
                            </Control>
                            {
                                this.state.dateToInvalid ? <InputWarning>Invalid date.</InputWarning> : <></>
                            }
                        </Field>
                        <Spacer/>
                    </React.Fragment> : <></>
                }
                <SectionName>Location:</SectionName>
                {
                    this.state.selectedLocation ? <div>
                        {this.state.selectedLocation.name}
                    </div> : <div
                        style={this.state.locationInvalid ? this.locationInvalidFieldStyle : undefined}
                    >Location is not selected.</div>
                }
                {
                    this.state.locationInvalid ? <InputWarning>Please select location.</InputWarning> : <></>
                }
                <LocationContainer>
                    <SmallSection>Search location</SmallSection>
                    <Field>
                        <Control>
                            <Input
                                type="text"
                                placeholder="Search location"
                                value={this.state.locationSearchText}
                                onFocus={this.handleLocationFieldFocus}
                                onChange={this.handleLocationSearchTextChange}
                            />
                        </Control>
                    </Field>
                    {
                        this.state.loadingLocations ? <div>
                            <span>Searching locations...</span>
                            <Icon className="fas fa-spinner fa-spin"/>
                        </div> : <></>
                    }
                    {
                        (this.state.showSearchResult && !this.state.loadingLocations && this.state.locations.length > 0) ?
                            <SearchResultContainer>
                                <SmallSection>Select Location</SmallSection>
                                {
                                    this.state.locations.map((location: any) => {
                                        return <SearchResult
                                            key={location.id}
                                            onClick={() => {
                                                this.selectLocation(location);
                                            }}
                                        >
                                            {location.name}
                                        </SearchResult>;
                                    })
                                }
                            </SearchResultContainer> : <></>
                    }
                </LocationContainer>
                <Spacer/>
                {
                    !this.state.loading && this.state.result ? <React.Fragment>
                        <SectionName>Units</SectionName>
                        <Field>
                            <Control>
                                <Radio name="celsius" checked={this.state.unitType === "celsius"}
                                       onChange={this.handleSelectUnitTypeCelsius}> Celsius </Radio>
                                <Radio name="fahrenheit" checked={this.state.unitType === "fahrenheit"}
                                       onChange={this.handleSelectUnitTypeFahrenheit}> Fahrenheit </Radio>
                            </Control>
                        </Field>
                        <Spacer/>
                    </React.Fragment> : <></>
                }
                {
                    this.state.showRefreshButton ? <div>
                        <Button onClick={this.onRefresh} style={{marginRight: '10px'}}>
                            <Icon className="fas fa-sync-alt" style={{marginRight: '5px'}}/>
                            {"Refresh"}
                        </Button>
                        <Button onClick={this.onReset}>Reset</Button>
                        <Spacer/>
                    </div> : <></>
                }
                {
                    this.state.loading && <div style={{textAlign: 'center'}}>
                        <span>Loading...</span>
                        <Icon className="fas fa-spinner fa-spin"/>
                    </div>
                }
                {
                    !this.state.loading && this.state.errorMessage ? <div>
                        <InformationCard>{this.state.errorMessage}</InformationCard>
                    </div> : <></>
                }
                {
                    !this.state.loading && this.state.result && this.state.result.location ?
                            <InformationCard style={{padding: '20px'}}>
                                <div>
                                    {`${this.state.result.location.name}, ${this.state.result.location.region}, ${this.state.result.location.country}`}
                                </div>
                                <div style={{fontSize: '14px'}}>
                                    {`Local Time: ${this.state.result.location.localtime}`}
                                </div>
                                <Spacer/>
                                {
                                    (this.state.option === 'realtime' || this.state.option === 'forecast') && this.state.result.current ? <React.Fragment>
                                        <SectionName>Today:</SectionName>
                                        <WeatherRealtime
                                            data={this.state.result.current}
                                            unitType={this.state.unitType}
                                        />
                                    </React.Fragment> : <></>
                                }
                                {
                                    (this.state.option === 'forecast' || this.state.option === 'history') &&
                                    this.state.result.forecast && this.state.result.forecast.forecastday ? <React.Fragment>
                                        <Spacer/>
                                        {
                                            this.state.result.forecast.forecastday.map((fd: any, index: any) => {
                                                return <WeatherForecast
                                                        key={index}
                                                        date={fd.date}
                                                        data={fd.day}
                                                        unitType={this.state.unitType}
                                                        showSeparator={index < this.state.result.forecast.forecastday.length - 1}
                                                    />;
                                            })
                                        }
                                    </React.Fragment> : <></>
                                }
                            </InformationCard> : <></>
                }
                <Spacer/>
            </Card>
        </ModelContentContainer>;
    }
}

export default WeatherModel;


// WEBPACK FOOTER //
// ./src/routes/Dashboard/Weather/WeatherModel.tsx